import React from 'react';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { RmdDashboardLoading } from './RmdDashboard.loading';
import { HtmlTable } from '../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { formatCurrency } from '../../../CommonUtils/formatNumber';
import { useRouteParams } from '../../hooks/useRouteParams';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { TruncatedText } from '../../../CommonComponents/OPL/Text/TruncatedText';
import { PageWrapper } from '../../../CommonComponents/PageWrapper/PageWrapper';
import { ColumnChart } from '../../../CommonComponents/OPL/charts/ColumnChart/ColumnChart';
import { useRmdDashboard } from './useRmdDashboard';
import { Dot } from '../../../CommonComponents/OPL/Dot/Dot';
import { TabSlug } from '../../types';

export const RmdDashboard = () => {
  const { clientId, accountId, groupId, isApex: isApexRouteParam } = useRouteParams();
  const isApex =
    isApexRouteParam && isApexRouteParam !== 'undefined' ? JSON.parse(isApexRouteParam) : false;

  return (
    <PageWrapper>
      <Box display="flex" flexDirection="column" css="gap: 24px" mt="24">
        <Button
          variant="link"
          leftIcon="fas fa-chevron-left"
          onClick={() => {
            ADV.util.Router.go2('AccountPerformanceReporting', {
              clientId,
              groupId,
              accountId,
              tabSlug: TabSlug.Summary,
              isApex,
            });
          }}
          css="align-self: flex-start"
        >
          Back to Summary
        </Button>

        <Text fontWeight={600} fontSize="h6">
          RMD Summary
        </Text>

        <RmdDashboardContent />
      </Box>
    </PageWrapper>
  );
};

const RmdDashboardContent = () => {
  const { isLoading, isError, refetch, data } = useRmdDashboard();

  if (isLoading) {
    return <RmdDashboardLoading />;
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" my="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    throw new Error('No data in RmdDashboard');
  }

  if (data.chartData.series.length === 0) {
    return (
      <RmdTable>
        <EmptyState />
      </RmdTable>
    );
  }

  const { isRegistrationGrouping, chartData } = data;

  return (
    <Box display="flex" flexDirection="column" css="gap: 24px">
      <ColumnChart
        series={[
          {
            data: chartData.series.map(({ color, name, total }) => ({
              color,
              name,
              y: total,
            })),
          },
        ]}
        categories={chartData.categories}
        yAxisType="currency"
        customSettings={{
          legend: {
            enabled: false,
          },
          tooltip: {
            shared: true,

            formatter() {
              let tooltip = `
              <span style="font-size: 10px">
                ${(this as any).x}
              </span><table>
            `;

              if ((this as any).points) {
                (this as any).points.forEach(({ y, color }) => {
                  tooltip += `
                  <tr>
                    <td>
                      <span style="color:${color}">\u25CF</span>
                    </td>
                    <td>
                      <span style="font-weight: 600">
                        ${formatCurrency(Number(y), { mantissa: 2 })}
                      </span>
                    </td>
                  </tr>
                `;
                });
              }

              return `${tooltip}</table>`;
            },
          },
        }}
      />

      <RmdTable isRegistrationGrouping={isRegistrationGrouping}>
        {chartData.series.map(account => (
          <tr key={account.accountNumber}>
            <td css="display: flex; align-items: center">
              <Dot bg={account.color || undefined} mr="8" />
              <TruncatedText fontWeight={500}>{account.name}</TruncatedText>
            </td>

            {!isRegistrationGrouping && (
              <td>
                <TruncatedText width="100%" textAlign="right">
                  {account.accountNumber}
                </TruncatedText>
              </td>
            )}

            <td>
              <Text variant="tabular" fontWeight={500} textAlign="right">
                {formatCurrency(account.total, { mantissa: 2 })}
              </Text>
            </td>

            <td>
              <Text variant="tabular" fontWeight={500} textAlign="right">
                {formatCurrency(account.ytdDistribution, { mantissa: 2 })}
              </Text>
            </td>

            <td>
              <Text variant="tabular" fontWeight={500} textAlign="right">
                {formatCurrency(account.remaining, { mantissa: 2 })}
              </Text>
            </td>
          </tr>
        ))}
      </RmdTable>
    </Box>
  );
};

interface RmdTableProps {
  isRegistrationGrouping?: boolean;
  children: React.ReactNode;
}

const RmdTable = ({ isRegistrationGrouping, children }: RmdTableProps) => {
  return (
    <HtmlTable>
      <table css="table-layout: fixed">
        <colgroup>
          <col width="35%" />
        </colgroup>

        <thead>
          <tr>
            <th>
              <Text fontWeight={600} fontSize="small" textAlign="left" color="neutral.600">
                {isRegistrationGrouping ? 'Registration' : 'Account'}
              </Text>
            </th>

            {!isRegistrationGrouping && (
              <th>
                <Text fontWeight={600} fontSize="small" textAlign="right" color="neutral.600">
                  Account Number
                </Text>
              </th>
            )}

            <th>
              <Text fontWeight={600} fontSize="small" textAlign="right" color="neutral.600">
                Total
              </Text>
            </th>

            <th>
              <Text fontWeight={600} fontSize="small" textAlign="right" color="neutral.600">
                YTD Distribution
              </Text>
            </th>

            <th>
              <Text fontWeight={600} fontSize="small" textAlign="right" color="neutral.600">
                Remaining
              </Text>
            </th>
          </tr>
        </thead>

        <tbody>{children}</tbody>
      </table>
    </HtmlTable>
  );
};

const EmptyState = () => {
  return (
    <tr>
      <td colSpan={4}>
        <Text fontWeight={500}>There is no RMD information available.</Text>
      </td>
    </tr>
  );
};
